/**
 * Authentication Context - Centralized auth state management
 */

import type { UserSession } from '@refinevoice/shared';
import { API_ENDPOINTS } from '@refinevoice/shared';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect } from 'react';
import { useAuthStore } from '../store/useAuthStore';

interface AuthContextType {
  userSession: UserSession | null;
  isRegistered: boolean;
  isAuthenticated: boolean;
  email: string | undefined;
  signOut: () => Promise<void>;
  requestMagicLink: (email: string) => Promise<boolean>;
  verifyMagicLinkToken: (token: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { userSession, setUserSession, showError } = useAuthStore();

  const verifyMagicLinkToken = useCallback(
    async (token: string): Promise<boolean> => {
      try {
        const response = await fetch(API_ENDPOINTS.AUTH_VERIFY, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to verify magic link');
        }

        const data = await response.json();

        const newUserSession: UserSession = {
          type: 'registered',
          email: data.email,
        };

        setUserSession(newUserSession);
        showError('Welcome Back!', `Successfully signed in as ${data.email}.`, 'info');
        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        showError('Sign-in Failed', `Could not verify magic link. ${errorMessage}`);
        return false;
      }
    },
    [setUserSession, showError]
  );

  // Initialize auth state once on mount
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Check for existing session via secure cookie
        const response = await fetch(API_ENDPOINTS.AUTH_ME, {
          credentials: 'include',
        });

        if (response.ok && isMounted) {
          const data = await response.json();
          if (data.user) {
            setUserSession(data.user);
          }

          // If server indicates cookie was cleared, force a client-side cleanup
          if (data.clearCookie) {
            // Force a brief delay to ensure cookie is cleared before any subsequent requests
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }

        // Handle magic link from URL parameters
        if (isMounted) {
          const urlParams = new URLSearchParams(window.location.search);
          const token = urlParams.get('token');
          const email = urlParams.get('email');

          if (token && email) {
            // Clear URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);
            // Verify magic link and redirect to account on success
            const success = await verifyMagicLinkToken(token);
            if (success) {
              window.location.href = '/account';
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to initialize auth:', error);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [setUserSession, verifyMagicLinkToken]);

  const requestMagicLink = async (email: string): Promise<boolean> => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH_REQUEST, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send magic link');
      }

      await response.json();
      showError('Check Your Email', `We've sent a sign-in link to ${email}.`, 'info');
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showError('Sign-in Failed', `Could not send magic link. ${errorMessage}`);
      return false;
    }
  };

  const signOut = async () => {
    try {
      await fetch(API_ENDPOINTS.AUTH_SIGNOUT, {
        method: 'POST',
        credentials: 'include',
      });

      setUserSession(null);
      showError('Signed Out', 'You have been successfully signed out.', 'info');
    } catch (error) {
      console.error('Sign out error:', error);
      setUserSession(null);
      showError('Signed Out', 'You have been signed out.', 'info');
    }
  };

  const isRegistered = userSession?.type === 'registered';

  const contextValue: AuthContextType = {
    userSession,
    isRegistered,
    isAuthenticated: isRegistered,
    email: userSession?.email,
    signOut,
    requestMagicLink,
    verifyMagicLinkToken,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
